import { useState } from 'react';
import { FileText, Edit3 } from 'lucide-react';
import PDFUploader from '../components/PDFUploader';
import ManualTripCreator from '../components/ManualTripCreator';
import QuickSetupPrompt from '../components/QuickSetupPrompt';
import { colors, radii, shadows, typography, fontWeights, spacing } from '../styles/designTokens';

function CreateTripPage({ onTripCreated, authToken, user }) {
  const [creationMode, setCreationMode] = useState('pdf'); // 'manual' or 'pdf'
  const [showQuickSetup, setShowQuickSetup] = useState(false);
  const [createdTrip, setCreatedTrip] = useState(null);

  const handlePDFTripCreated = (trip) => {
    setCreatedTrip(trip);
    setShowQuickSetup(true);
  };

  const handleQuickSetupComplete = (_prefs) => {
    setShowQuickSetup(false);
    if (onTripCreated) {
      onTripCreated(createdTrip);
    }
  };

  const handleQuickSetupDismiss = () => {
    setShowQuickSetup(false);
    if (onTripCreated) {
      onTripCreated(createdTrip);
    }
  };

  return (
    <div style={{ padding: '20px' }}>
      {/* Mode Toggle */}
      <div style={{
        background: colors.surface,
        borderRadius: radii.card,
        padding: '20px',
        boxShadow: shadows.card,
        marginBottom: '20px'
      }}>
        <h1 style={{
          fontSize: '28px',
          fontWeight: fontWeights.bold,
          color: colors.textPrimary,
          margin: '0 0 16px 0'
        }}>
          Create a New Trip
        </h1>
        
        <p style={{
          color: colors.textSecondary,
          fontSize: typography.body.fontSize,
          margin: '0 0 20px 0'
        }}>
          Choose how you'd like to create your trip
        </p>

        <div style={{
          display: 'flex',
          gap: spacing.cardGap,
          background: colors.surfaceAlt,
          padding: '4px',
          borderRadius: radii.inner,
          width: 'fit-content'
        }}>
          <button
            onClick={() => setCreationMode('pdf')}
            style={{
              padding: '10px 20px',
              fontSize: typography.body.fontSize,
              fontWeight: fontWeights.medium,
              color: creationMode === 'pdf' ? 'white' : colors.textSecondary,
              background: creationMode === 'pdf' ? colors.info : 'transparent',
              border: 'none',
              borderRadius: radii.inner,
              cursor: 'pointer',
              transition: 'all 0.2s',
              display: 'flex',
              alignItems: 'center',
              gap: spacing.elementGap
            }}
          >
            <FileText size={16} />
            Upload PDF
          </button>
          
          <button
            onClick={() => setCreationMode('manual')}
            style={{
              padding: '10px 20px',
              fontSize: typography.body.fontSize,
              fontWeight: fontWeights.medium,
              color: creationMode === 'manual' ? 'white' : colors.textSecondary,
              background: creationMode === 'manual' ? colors.info : 'transparent',
              border: 'none',
              borderRadius: radii.inner,
              cursor: 'pointer',
              transition: 'all 0.2s',
              display: 'flex',
              alignItems: 'center',
              gap: spacing.elementGap
            }}
          >
            <Edit3 size={16} />
            Manual Entry
          </button>
        </div>
      </div>

      {/* Manual Trip Creator */}
      {creationMode === 'manual' && (
        <ManualTripCreator 
          onTripCreated={onTripCreated} 
          user={user} 
        />
      )}

      {/* PDF Upload */}
      {creationMode === 'pdf' && (
        <>
          <div style={{
            background: colors.surface,
            borderRadius: radii.card,
            padding: '40px',
            boxShadow: shadows.card,
            marginBottom: '20px'
          }}>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: spacing.cardGap,
              marginBottom: '20px'
            }}>
              <FileText size={24} style={{ color: colors.urgent }} />
              <h2 style={{
                fontSize: '24px',
                fontWeight: fontWeights.semibold,
                color: colors.textPrimary,
                margin: '0'
              }}>
                Create Trip from PDF
              </h2>
            </div>
            
            <p style={{
              color: colors.textSecondary,
              fontSize: typography.subheading.fontSize,
              margin: '0 0 20px 0',
              lineHeight: '1.5'
            }}>
              Upload your travel confirmation PDF to automatically extract flight details, hotel information, and car rental details.
            </p>
          </div>
          
          <PDFUploader onTripCreated={handlePDFTripCreated} authToken={authToken} user={user} />
        </>
      )}

      {showQuickSetup && createdTrip && (
        <QuickSetupPrompt
          tripId={createdTrip.tripId}
          userId={createdTrip.userId}
          flights={createdTrip.flights || []}
          onComplete={handleQuickSetupComplete}
          onDismiss={handleQuickSetupDismiss}
        />
      )}
    </div>
  );
}

export default CreateTripPage;
